// AuthorityStatement.jsx
// ─────────────────────────────────────────────────────────────────────────────
// A quiet, weighted positional statement that sits between Publications and
// Voices. Answers the unspoken vendor question:
//
//   "Will I be one of many, or one of few?"
//
// Visual: minimal section, large italic serif statement, gold rules above
// and below, no imagery. Reads like a magazine epigraph or the editorial
// page-break callout in a luxury journal.
// ─────────────────────────────────────────────────────────────────────────────

import { AUTHORITY_STATEMENT, getPalette } from '../data/inclusionContent.js';

const GD   = 'var(--font-heading-primary)';
const FS   = 'var(--font-serif-body, Georgia, "Times New Roman", serif)';
const NU   = 'var(--font-body)';
const GOLD = '#c9a96e';

export default function AuthorityStatement({ darkMode }) {
  const P = getPalette(darkMode);

  return (
    <section style={{
      background:  P.sectionBg,
      // Slightly more vertical breathing room, the line needs space
      // around it to land like an editorial epigraph rather than a
      // section subtitle.
      padding:     'clamp(96px, 12vw, 160px) clamp(24px, 6vw, 80px)',
      transition:  'background 0.3s ease',
    }}>
      <div style={{
        maxWidth: 960,
        margin:   '0 auto',
        textAlign: 'center',
      }}>
        {/* Top gold rule, wide, fading at the ends */}
        <div aria-hidden style={{
          width:  'clamp(100px, 16vw, 220px)',
          height: 1,
          margin: '0 auto 56px',
          background: `linear-gradient(to right, transparent, ${GOLD}, transparent)`,
        }} />

        {/* The statement, large italic serif, magazine-epigraph feel.
            Sized up so the line carries the editorial weight a positional
            beat demands; this is the page's quiet authority moment and
            it should read as a stated fact, not a caption. */}
        <p style={{
          fontFamily: GD,
          fontStyle:  'italic',
          fontSize:   'clamp(26px, 3.2vw, 44px)',
          fontWeight: 400,
          lineHeight: 1.35,
          color:      P.text,
          margin:     0,
          letterSpacing: '-0.01em',
          transition: 'color 0.3s ease',
        }}>
          {AUTHORITY_STATEMENT.text}
        </p>

        {/* Attribution, tiny gold caps */}
        {AUTHORITY_STATEMENT.attribution && (
          <p style={{
            fontFamily:    NU,
            fontSize:      9,
            fontWeight:    600,
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            color:         GOLD,
            marginTop:     40,
            marginBottom:  0,
          }}>
           , {AUTHORITY_STATEMENT.attribution}
          </p>
        )}

        {/* Bottom gold rule */}
        <div aria-hidden style={{
          width:  'clamp(100px, 16vw, 220px)',
          height: 1,
          margin: '56px auto 0',
          background: `linear-gradient(to right, transparent, ${GOLD}, transparent)`,
        }} />
      </div>
    </section>
  );
}
